import styled from "@emotion/styled";
import React from "react";
import { Typography } from "../Typography";
import { theme } from "../theme";
import { StatusVariant } from "@ecologi/shared";
import InfoIcon from "@ecologi/assets/icons/info.svg";
import { Tooltip } from "../Tooltip";

type Props = {
  label: string;
  variant: StatusVariant;
  rounded?: boolean;
  showIcon?: boolean;
  size?: "small" | "large";
  helpText?: string;
};

interface StatusVariantColours {
  background: string;
  text: string;
}

const LabelIconContainer = styled.div({
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
});

const StyledInfoIcon = styled(InfoIcon)({
  fill: theme.colors.forest,
});

const variantColourMap: Record<StatusVariant, StatusVariantColours> = {
  bold: {
    background: theme.colors.charcoal,
    text: theme.colors.snowdrop,
  },
  info: {
    background: theme.colors.mist,
    text: theme.colors.charcoal,
  },
  warning: {
    background: theme.colors.roseLight,
    text: theme.colors.charcoal,
  },
  feature: {
    background: theme.colors.forestLight,
    text: theme.colors.snowdrop,
  },
};

const StyledTypography = styled(Typography)<{
  variant: StatusVariant;
  rounded?: boolean;
}>(({ variant, rounded = true }) => ({
  backgroundColor: variantColourMap[variant].background,
  color: variantColourMap[variant].text,
  borderRadius: rounded ? "40px" : "0px",
  padding: rounded ? "6px 12px" : "3px 8px",
}));

export const StatusTag = ({ helpText, ...props }: Props) => {
  if (helpText) {
    return (
      <Tooltip content={helpText} place="bottom-start">
        <Content {...props} />
      </Tooltip>
    );
  }
  return <Content {...props} />;
};

const Content = ({ label, size = "small", helpText, ...props }: Props) => {
  return (
    <StyledTypography
      use={size === "small" ? "dashboardMicroCopyRegular" : "dashboardTitle3"}
      as="strong"
      {...props}
    >
      <LabelIconContainer>
        {label}
        {helpText ? <StyledInfoIcon /> : null}
      </LabelIconContainer>
    </StyledTypography>
  );
};
